import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import AdminLayout from './AdminLayout';
import '../App.css';

const EditProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [profileId, setProfileId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    party_name: '',
    logo_url: '',
    profile_picture: '',
    flash_image: '',
    address: '',
    mobile_number: '',
    email_id: '',
    about_us: '',
    description: '',
    slider_images: [],
    image_gallery: [],
    video_gallery: [],
    instagram_reels: [],
    recent_news: [],
    social_links: {
      facebook: '',
      twitter: '',
      instagram: '',
      website: '',
      youtube: '',
      linkedin: ''
    },
    surveys: []
  });
  
  // Temporary state for adding items to arrays
  const [newSliderImage, setNewSliderImage] = useState('');
  const [newGalleryImage, setNewGalleryImage] = useState('');
  const [newVideo, setNewVideo] = useState({ title: '', url: '' });
  const [newInstagramReel, setNewInstagramReel] = useState({ title: '', url: '' });
  const [newNews, setNewNews] = useState({ title: '', description: '', date: '', image: '' });
  const [newsImageFile, setNewsImageFile] = useState(null);
  const [newsImagePreview, setNewsImagePreview] = useState('');
  const [uploadingNewsImage, setUploadingNewsImage] = useState(false);
  const uploadedImageUrlRef = useRef(''); // Ref to track uploaded image URL
  const [newSurvey, setNewSurvey] = useState({ title: '', description: '', questions: [] });
  const [newQuestion, setNewQuestion] = useState({ question: '', type: 'text', options: [] });
  const [newOption, setNewOption] = useState('');
  const [editingSurveyIndex, setEditingSurveyIndex] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState('');
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  const [flashImageFile, setFlashImageFile] = useState(null);
  const [flashImagePreview, setFlashImagePreview] = useState('');
  const [uploadingFlash, setUploadingFlash] = useState(false);
  const [sliderFiles, setSliderFiles] = useState([]);
  const [uploadingSlider, setUploadingSlider] = useState(false);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoadingProfile(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/profiles/username/${user?.username}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success && data.data) {
        const profile = data.data;
        setProfileId(profile.id);
        // Parse JSON fields if they come as strings
        const parseJsonField = (field) => {
          if (Array.isArray(field)) return field;
          if (typeof field === 'string') {
            try {
              return JSON.parse(field);
            } catch (e) {
              return [];
            }
          }
          return field || [];
        };

        const parseJsonObject = (field) => {
          if (typeof field === 'object' && field !== null) return field;
          if (typeof field === 'string') {
            try {
              return JSON.parse(field);
            } catch (e) {
              return {};
            }
          }
          return field || {};
        };

        const parsedRecentNews = parseJsonField(profile.recent_news);
        
        // Debug: Log recent_news to verify image field
        console.log('Parsed recent_news:', parsedRecentNews);
        if (parsedRecentNews.length > 0) {
          console.log('First news item:', parsedRecentNews[0]);
          console.log('First news item image:', parsedRecentNews[0].image);
        }

        // Parse surveys field
        let parsedSurveys = [];
        if (profile.surveys) {
          if (Array.isArray(profile.surveys)) {
            parsedSurveys = profile.surveys;
          } else if (typeof profile.surveys === 'string') {
            try {
              parsedSurveys = JSON.parse(profile.surveys);
              if (!Array.isArray(parsedSurveys)) {
                parsedSurveys = [];
              }
            } catch (e) {
              console.error('Error parsing surveys:', e);
              parsedSurveys = [];
            }
          } else if (typeof profile.surveys === 'object' && profile.surveys !== null) {
            parsedSurveys = Array.isArray(profile.surveys) ? profile.surveys : [];
          }
        }
        console.log('Raw surveys from profile:', profile.surveys);
        console.log('Parsed surveys:', parsedSurveys);
        console.log('Surveys type:', typeof parsedSurveys);
        console.log('Surveys is array:', Array.isArray(parsedSurveys));
        console.log('Surveys length:', parsedSurveys?.length);

        setFormData({
          name: profile.name || '',
          party_name: profile.party_name || '',
          logo_url: profile.logo_url || '',
          profile_picture: profile.profile_picture || '',
          flash_image: profile.flash_image || '',
          address: profile.address || '',
          mobile_number: profile.mobile_number || '',
          email_id: profile.email_id || '',
          about_us: profile.about_us || '',
          description: profile.description || '',
          slider_images: parseJsonField(profile.slider_images),
          image_gallery: parseJsonField(profile.image_gallery),
          video_gallery: parseJsonField(profile.video_gallery),
          instagram_reels: parseJsonField(profile.instagram_reels),
          recent_news: parsedRecentNews,
          surveys: parsedSurveys,
          social_links: parseJsonObject(profile.social_links) || {
            facebook: '',
            twitter: '',
            instagram: '',
            website: '',
            youtube: '',
            linkedin: ''
          }
        });
        if (profile.logo_url) {
          setLogoPreview(profile.logo_url);
        }
        if (profile.profile_picture) {
          setProfilePicturePreview(profile.profile_picture);
        }
        if (profile.flash_image) {
          setFlashImagePreview(`${API_BASE_URL}${profile.flash_image}`);
        }
      } else {
        setMessage({ type: 'error', text: 'Profile not found. Please create a profile first.' });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setMessage({ type: 'error', text: 'Error loading profile. Please try again.' });
    } finally {
      setLoadingProfile(false);
    }
  };


  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size should be less than 5MB' });
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload logo
  const handleLogoUpload = async () => {
    if (!logoFile) {
      setMessage({ type: 'error', text: 'Please select a logo file' });
      return;
    }
    setUploadingLogo(true);
    setMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('logo', logoFile);
      const response = await fetch(`${API_BASE_URL}/api/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      if (response.ok && data.success) {
        const logoUrl = `${API_BASE_URL}${data.data.url}`;
        // Update formData with new logo URL
        setFormData(prevData => ({
          ...prevData,
          logo_url: logoUrl
        }));
        // Update preview as well
        setLogoPreview(logoUrl);
        setMessage({ type: 'success', text: 'Logo uploaded successfully! You can now update the profile.' });
        console.log('Logo uploaded, new URL:', logoUrl); // Debug log
      } else {
        setMessage({ type: 'error', text: data.message || 'Error uploading logo' });
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage({ type: 'error', text: 'Error uploading logo. Please try again.' });
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handle profile picture file selection
  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size should be less than 5MB' });
        return;
      }
      setProfilePictureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload profile picture
  const handleProfilePictureUpload = async () => {
    if (!profilePictureFile) {
      setMessage({ type: 'error', text: 'Please select a profile picture file' });
      return;
    }
    
    setUploadingProfilePicture(true);
    setMessage({ type: '', text: '' });
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage({ type: 'error', text: 'Please login to upload images' });
        setUploadingProfilePicture(false);
        return;
      }

      const uploadFormData = new FormData();
      uploadFormData.append('logo', profilePictureFile); // Reuse logo upload endpoint

      console.log('Starting upload...', { fileName: profilePictureFile.name, fileSize: profilePictureFile.size });

      const response = await fetch(`${API_BASE_URL}/api/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadFormData
      });

      console.log('Response status:', response.status);

      // Check if response is ok before parsing JSON
      let data;
      try {
        data = await response.json();
        console.log('Response data:', data);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        const text = await response.text();
        console.error('Response text:', text);
        setMessage({ type: 'error', text: 'Invalid response from server. Please try again.' });
        setUploadingProfilePicture(false);
        return;
      }

      if (response.ok && data.success && data.data && data.data.url) {
        const pictureUrl = `${API_BASE_URL}${data.data.url}`;
        console.log('Upload successful! URL:', pictureUrl);
        
        setFormData(prevData => ({
          ...prevData,
          profile_picture: pictureUrl
        }));
        setProfilePicturePreview(pictureUrl);
        setMessage({ 
          type: 'success', 
          text: '✅ Profile picture uploaded successfully! Now click "Update Profile" button to save it.' 
        });
        
        // Scroll to top to show message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        console.error('Upload failed:', data);
        const errorMsg = data.message || data.error || 'Error uploading profile picture';
        setMessage({ type: 'error', text: '❌ ' + errorMsg });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setMessage({ type: 'error', text: `Error uploading profile picture: ${error.message}` });
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  // Handle flash image file selection
  const handleFlashImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size should be less than 5MB' });
        return;
      }

      setFlashImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFlashImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload flash image
  const handleFlashImageUpload = async () => {
    if (!flashImageFile) {
      setMessage({ type: 'error', text: 'Please select a flash image file' });
      return;
    }

    setUploadingFlash(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage({ type: 'error', text: 'Please login to upload images' });
        setUploadingFlash(false);
        return;
      }

      const uploadFormData = new FormData();
      uploadFormData.append('image', flashImageFile);

      const response = await fetch(`${API_BASE_URL}/api/upload/flash`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        setMessage({ type: 'error', text: 'Invalid response from server. Please try again.' });
        setUploadingFlash(false);
        return;
      }

      if (response.ok && data.success && data.data && data.data.url) {
        // Set the flash image URL from server response
        setFormData(prevData => ({
          ...prevData,
          flash_image: data.data.url // Store relative URL
        }));
        
        setFlashImagePreview(`${API_BASE_URL}${data.data.url}`);
        setMessage({ 
          type: 'success', 
          text: '✅ Flash image uploaded successfully! Now click "Update Profile" button to save it.' 
        });
        
        // Scroll to top to show message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Clear file after successful upload
        setTimeout(() => {
          setFlashImageFile(null);
        }, 2000);
      } else {
        const errorMsg = data.message || data.error || 'Error uploading flash image';
        setMessage({ type: 'error', text: '❌ ' + errorMsg });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error uploading flash image:', error);
      setMessage({ type: 'error', text: 'Error uploading flash image. Please try again.' });
    } finally {
      setUploadingFlash(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('social_')) {
      const field = name.replace('social_', '');
      setFormData({
        ...formData,
        social_links: { 
          ...(formData.social_links || {
            facebook: '',
            twitter: '',
            instagram: '',
            website: '',
            youtube: '',
            linkedin: ''
          }), 
          [field]: value 
        }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Slider images handlers
  const handleSliderFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const validFiles = files.filter(file => {
        if (!file.type.match('image.*')) {
          setMessage({ type: 'error', text: 'Please select only image files' });
          return false;
        }
        if (file.size > 5 * 1024 * 1024) {
          setMessage({ type: 'error', text: 'File size should be less than 5MB' });
          return false;
        }
        return true;
      });
      setSliderFiles([...sliderFiles, ...validFiles]);
      e.target.value = '';
    }
  };

  const handleSliderUpload = async () => {
    if (sliderFiles.length === 0) {
      setMessage({ type: 'error', text: 'Please select slider images' });
      return;
    }
    setUploadingSlider(true);
    setMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      const uploadedUrls = [];
      for (const file of sliderFiles) {
        const formData = new FormData();
        formData.append('image', file);
        const response = await fetch(`${API_BASE_URL}/api/upload/slider`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const data = await response.json();
        if (response.ok && data.success) {
          uploadedUrls.push(`${API_BASE_URL}${data.data.url}`);
        }
      }
      setFormData({ ...formData, slider_images: [...(formData.slider_images || []), ...uploadedUrls] });
      setSliderFiles([]);
      setMessage({ type: 'success', text: `${uploadedUrls.length} slider image(s) uploaded successfully!` });
    } catch (error) {
      console.error('Error uploading slider images:', error);
      setMessage({ type: 'error', text: 'Error uploading slider images' });
    } finally {
      setUploadingSlider(false);
    }
  };

  const removeSliderImage = (index) => {
    setFormData({ ...formData, slider_images: (formData.slider_images || []).filter((_, i) => i !== index) });
  };

  const removeSliderFile = (index) => {
    setSliderFiles(sliderFiles.filter((_, i) => i !== index));
  };

  // Gallery images handlers
  const handleGalleryFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const validFiles = files.filter(file => {
        if (!file.type.match('image.*')) {
          setMessage({ type: 'error', text: 'Please select only image files' });
          return false;
        }
        if (file.size > 5 * 1024 * 1024) {
          setMessage({ type: 'error', text: 'File size should be less than 5MB' });
          return false;
        }
        return true;
      });
      setGalleryFiles([...galleryFiles, ...validFiles]);
      e.target.value = '';
    }
  };

  const handleGalleryUpload = async () => {
    if (galleryFiles.length === 0) {
      setMessage({ type: 'error', text: 'Please select gallery images' });
      return;
    }
    setUploadingGallery(true);
    setMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      const uploadedUrls = [];
      for (const file of galleryFiles) {
        const formData = new FormData();
        formData.append('image', file);
        const response = await fetch(`${API_BASE_URL}/api/upload/gallery`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const data = await response.json();
        if (response.ok && data.success) {
          uploadedUrls.push(`${API_BASE_URL}${data.data.url}`);
        }
      }
      setFormData({ ...formData, image_gallery: [...(formData.image_gallery || []), ...uploadedUrls] });
      setGalleryFiles([]);
      setMessage({ type: 'success', text: `${uploadedUrls.length} gallery image(s) uploaded successfully!` });
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      setMessage({ type: 'error', text: 'Error uploading gallery images' });
    } finally {
      setUploadingGallery(false);
    }
  };

  const removeGalleryImage = (index) => {
    setFormData({ ...formData, image_gallery: (formData.image_gallery || []).filter((_, i) => i !== index) });
  };

  const removeGalleryFile = (index) => {
    setGalleryFiles(galleryFiles.filter((_, i) => i !== index));
  };

  // Video handlers
  const addVideo = () => {
    if (newVideo.title.trim() && newVideo.url.trim()) {
      setFormData({ ...formData, video_gallery: [...(formData.video_gallery || []), { ...newVideo }] });
      setNewVideo({ title: '', url: '' });
    }
  };

  const removeVideo = (index) => {
    setFormData({ ...formData, video_gallery: (formData.video_gallery || []).filter((_, i) => i !== index) });
  };

  // Instagram Reel handlers
  const addInstagramReel = () => {
    if (newInstagramReel.title.trim() && newInstagramReel.url.trim()) {
      setFormData({ ...formData, instagram_reels: [...(formData.instagram_reels || []), { ...newInstagramReel }] });
      setNewInstagramReel({ title: '', url: '' });
    }
  };

  const removeInstagramReel = (index) => {
    setFormData({ ...formData, instagram_reels: (formData.instagram_reels || []).filter((_, i) => i !== index) });
  };

  // News handlers
  // Handle news image file selection
  const handleNewsImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size should be less than 5MB' });
        return;
      }
      setNewsImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewsImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload news image
  const handleNewsImageUpload = async () => {
    if (!newsImageFile) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }
    setUploadingNewsImage(true);
    setMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage({ type: 'error', text: 'Please login to upload images' });
        setUploadingNewsImage(false);
        return;
      }
      const uploadFormData = new FormData();
      uploadFormData.append('logo', newsImageFile);
      const response = await fetch(`${API_BASE_URL}/api/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadFormData
      });
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        setMessage({ type: 'error', text: 'Invalid response from server. Please try again.' });
        setUploadingNewsImage(false);
        return;
      }
      if (response.ok && data.success && data.data && data.data.url) {
        const imageUrl = `${API_BASE_URL}${data.data.url}`;
        console.log('News image uploaded successfully. URL:', imageUrl);
        console.log('Setting newNews.image to:', imageUrl);
        
        // Store in ref for immediate access
        uploadedImageUrlRef.current = imageUrl;
        
        setNewNews(prevNews => {
          const updatedNews = { ...prevNews, image: imageUrl };
          console.log('Updated newNews with image:', updatedNews);
          console.log('Verification - updatedNews.image:', updatedNews.image);
          return updatedNews;
        });
        
        // Keep preview visible with the uploaded image URL
        setNewsImagePreview(imageUrl);
        setMessage({ type: 'success', text: 'News image uploaded successfully! Now add the news item.' });
        setNewsImageFile(null);
      } else {
        const errorMsg = data.message || data.error || 'Error uploading image';
        setMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Error uploading news image:', error);
      setMessage({ type: 'error', text: `Error uploading image: ${error.message}` });
    } finally {
      setUploadingNewsImage(false);
    }
  };

  const addNews = () => {
    if (newNews.title.trim() && newNews.description.trim()) {
      // Use functional update to get the latest state
      setNewNews(currentNews => {
        // Priority: 1. currentNews.image, 2. uploadedImageUrlRef, 3. newsImagePreview
        const imageUrl = currentNews.image || 
                        uploadedImageUrlRef.current || 
                        (newsImagePreview && newsImagePreview.startsWith('http') ? newsImagePreview : '');
        
        const newsItem = {
          title: currentNews.title,
          description: currentNews.description,
          date: currentNews.date || '',
          image: imageUrl
        };
        
        console.log('Adding news item:', newsItem);
        console.log('News image URL:', newsItem.image);
        console.log('Current newNews state:', currentNews);
        console.log('uploadedImageUrlRef.current:', uploadedImageUrlRef.current);
        console.log('newsImagePreview:', newsImagePreview);
        
        if (!newsItem.image) {
          console.warn('Warning: News item added without image');
        } else {
          console.log('✓ Image included in news item:', newsItem.image);
        }
        
        // Add to formData
        setFormData(prevFormData => ({
          ...prevFormData,
          recent_news: [...(prevFormData.recent_news || []), newsItem]
        }));
        
        // Reset state and ref
        setNewsImagePreview('');
        setNewsImageFile(null);
        uploadedImageUrlRef.current = '';
        
        // Return reset state
        return { title: '', description: '', date: '', image: '' };
      });
    } else {
      setMessage({ type: 'error', text: 'Please fill in title and description' });
    }
  };

  const removeNews = (index) => {
    setFormData({ ...formData, recent_news: (formData.recent_news || []).filter((_, i) => i !== index) });
  };

  // Survey handlers
  const addQuestion = () => {
    if (newQuestion.question.trim()) {
      setNewSurvey({
        ...newSurvey,
        questions: [...newSurvey.questions, { ...newQuestion }]
      });
      setNewQuestion({ question: '', type: 'text', options: [] });
      setNewOption('');
    }
  };

  const removeQuestion = (index) => {
    setNewSurvey({
      ...newSurvey,
      questions: newSurvey.questions.filter((_, i) => i !== index)
    });
  };

  const addOption = () => {
    if (newOption.trim() && newQuestion.type === 'multiple_choice') {
      setNewQuestion({
        ...newQuestion,
        options: [...newQuestion.options, newOption.trim()]
      });
      setNewOption('');
    }
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...newSurvey.questions];
    updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
    setNewSurvey({ ...newSurvey, questions: updatedQuestions });
  };

  const addSurvey = () => {
    if (editingSurveyIndex !== null) {
      // Update existing survey
      updateSurvey();
    } else {
      // Add new survey
      if (newSurvey.title.trim() && newSurvey.questions.length > 0) {
        const updatedSurveys = [...(formData.surveys || []), { ...newSurvey }];
        console.log('Adding survey:', newSurvey);
        console.log('Updated surveys array:', updatedSurveys);
        setFormData({
          ...formData,
          surveys: updatedSurveys
        });
        setNewSurvey({ title: '', description: '', questions: [] });
        setNewQuestion({ question: '', type: 'text', options: [] });
        setNewOption('');
        console.log('Survey added successfully. Total surveys:', updatedSurveys.length);
      } else {
        console.warn('Cannot add survey: Title or questions missing');
        console.log('Survey title:', newSurvey.title);
        console.log('Survey questions:', newSurvey.questions);
      }
    }
  };

  const removeSurvey = (index) => {
    setFormData({
      ...formData,
      surveys: (formData.surveys || []).filter((_, i) => i !== index)
    });
    if (editingSurveyIndex === index) {
      setEditingSurveyIndex(null);
      setNewSurvey({ title: '', description: '', questions: [] });
    }
  };

  const editSurvey = (index) => {
    const surveyToEdit = formData.surveys[index];
    setNewSurvey({ ...surveyToEdit });
    setEditingSurveyIndex(index);
    // Scroll to survey form
    setTimeout(() => {
      const surveyForm = document.getElementById('survey-form-section');
      if (surveyForm) {
        surveyForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const updateSurvey = () => {
    if (editingSurveyIndex !== null && newSurvey.title.trim() && newSurvey.questions.length > 0) {
      const updatedSurveys = [...formData.surveys];
      updatedSurveys[editingSurveyIndex] = { ...newSurvey };
      setFormData({ ...formData, surveys: updatedSurveys });
      setNewSurvey({ title: '', description: '', questions: [] });
      setNewQuestion({ question: '', type: 'text', options: [] });
      setNewOption('');
      setEditingSurveyIndex(null);
      setMessage({ type: 'success', text: 'Survey updated successfully!' });
    }
  };

  const cancelEdit = () => {
    setNewSurvey({ title: '', description: '', questions: [] });
    setNewQuestion({ question: '', type: 'text', options: [] });
    setNewOption('');
    setEditingSurveyIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.logo_url || formData.logo_url.trim() === '') {
      setMessage({ type: 'error', text: 'Please upload a logo image' });
      return;
    }
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      
      // Prepare update data with all fields
      const updateData = {
        name: formData.name,
        party_name: formData.party_name,
        logo_url: formData.logo_url,
        profile_picture: formData.profile_picture || null,
        flash_image: formData.flash_image || null,
        address: formData.address,
        mobile_number: formData.mobile_number,
        email_id: formData.email_id,
        about_us: formData.about_us,
        description: formData.description || '',
        slider_images: formData.slider_images || [],
        image_gallery: formData.image_gallery || [],
        video_gallery: formData.video_gallery || [],
        instagram_reels: formData.instagram_reels || [],
        recent_news: formData.recent_news || [],
        social_links: formData.social_links || {},
        surveys: formData.surveys || []
      };
      
      console.log('=== PROFILE UPDATE DEBUG ===');
      console.log('FormData instagram_reels:', formData.instagram_reels);
      console.log('FormData instagram_reels type:', typeof formData.instagram_reels);
      console.log('FormData instagram_reels is array:', Array.isArray(formData.instagram_reels));
      console.log('FormData instagram_reels length:', formData.instagram_reels?.length);
      console.log('UpdateData instagram_reels:', updateData.instagram_reels);
      console.log('UpdateData instagram_reels type:', typeof updateData.instagram_reels);
      console.log('UpdateData instagram_reels is array:', Array.isArray(updateData.instagram_reels));
      console.log('UpdateData instagram_reels length:', updateData.instagram_reels?.length);
      console.log('FormData surveys:', formData.surveys);
      console.log('FormData surveys type:', typeof formData.surveys);
      console.log('FormData surveys is array:', Array.isArray(formData.surveys));
      console.log('FormData surveys length:', formData.surveys?.length);
      console.log('UpdateData surveys:', updateData.surveys);
      console.log('UpdateData surveys type:', typeof updateData.surveys);
      console.log('UpdateData surveys is array:', Array.isArray(updateData.surveys));
      console.log('UpdateData surveys length:', updateData.surveys?.length);
      console.log('Instagram_reels (stringified):', JSON.stringify(updateData.instagram_reels, null, 2));
      console.log('Surveys (stringified):', JSON.stringify(updateData.surveys, null, 2));
      console.log('Full updateData:', JSON.stringify(updateData, null, 2));
      console.log('============================');
      
      const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      console.log('Update response:', data); // Debug log
      
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully! Redirecting...' });
        setTimeout(() => {
          navigate(`/profile/${user?.username}`);
        }, 1000);
      } else {
        setMessage({ type: 'error', text: data.message || 'Error updating profile. Please try again.' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Error updating profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    { icon: '🏠', label: 'Dashboard', path: '/dashboard', active: false },
    { icon: '📊', label: 'My Profile', path: `/profile/${user?.username}`, active: false },
    { icon: '📝', label: 'Create Profile', path: '/create-profile', active: false },
    { icon: '✏️', label: 'Edit Profile', path: '/edit-profile', active: true },
    { icon: '📈', label: 'Analytics', path: '/analytics', active: false },
    { icon: '📧', label: 'Messages', path: '/messages', active: false },
    { icon: '⚙️', label: 'Settings', path: '/settings', active: false }
  ];

  const isActive = (path) => location.pathname === path;

  if (loadingProfile) {
    return (
      <AdminLayout pageTitle="Edit Profile" pageSubtitle="Loading profile...">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
          <p className="mt-2 text-muted">Loading profile data...</p>
      </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Edit Profile" pageSubtitle="Update your profile information">
          {/* Edit Profile Form Card */}
          <div className="business-form-card">
            <div className="business-form-header">
              <div className="business-form-icon-wrapper">
                <i className="fi fi-rr-edit"></i>
          </div>
              <h2 className="business-form-title">Edit Profile</h2>
              <p className="business-form-subtitle">Modify your political profile details</p>
        </div>

            {message.text && (
              <div className={`business-alert business-alert-${message.type === 'success' ? 'success' : 'error'}`}>
                <i className={`fi fi-rr-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                <span>{message.text}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="business-create-form">
              {/* Logo & Images Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-picture"></i>
                  Logo & Images
                </h3>
                
                <div className="business-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                  {/* Logo Upload */}
                  <div className="business-form-group">
                    <label htmlFor="logo_file" className="business-form-label">
                      <i className="fi fi-rr-picture"></i>
                    Logo Image * (Max 5MB)
                  </label>
                    
                    <div className="business-upload-wrapper">
                      <input
                        type="file"
                        id="logo_file"
                        name="logo_file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="business-file-input"
                      />
                      <label htmlFor="logo_file" className="business-file-label">
                        <i className="fi fi-rr-cloud-upload"></i>
                        <span>{logoFile ? logoFile.name : 'Choose Logo Image'}</span>
                    </label>
                    {logoFile && (
                        <button
                          type="button"
                          onClick={handleLogoUpload}
                          disabled={uploadingLogo}
                          className="business-btn business-btn-primary business-upload-btn"
                        >
                          {uploadingLogo ? (
                            <>
                              <span className="business-spinner"></span>
                              <span>Uploading...</span>
                            </>
                          ) : (
                            <>
                              <i className="fi fi-rr-upload"></i>
                              <span>Upload</span>
                            </>
                          )}
                      </button>
                    )}
                  </div>

                    {/* Preview */}
                    {(logoPreview || formData.logo_url) && (
                      <div className="business-image-preview">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" className="business-preview-image" />
                        ) : formData.logo_url ? (
                          <>
                            <p className="business-upload-success">
                              <i className="fi fi-rr-check-circle"></i>
                              Logo uploaded successfully
                            </p>
                            <img src={formData.logo_url} alt="Uploaded logo" className="business-preview-image" />
                          </>
                        ) : null}
                    </div>
                  )}

                    <input
                      type="hidden"
                      name="logo_url"
                      value={formData.logo_url}
                      required={!formData.logo_url}
                    />
                </div>

                {/* Profile Picture Upload */}
                  <div className="business-form-group">
                    <label htmlFor="profile_picture_file" className="business-form-label">
                      <i className="fi fi-rr-user"></i>
                    Profile Picture (Max 5MB)
                  </label>
                  
                    <div className="business-upload-wrapper">
                    <input
                      type="file"
                      id="profile_picture_file"
                      name="profile_picture_file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                        className="business-file-input"
                    />
                      <label htmlFor="profile_picture_file" className="business-file-label">
                        <i className="fi fi-rr-cloud-upload"></i>
                        <span>{profilePictureFile ? profilePictureFile.name : 'Choose Profile Picture'}</span>
                    </label>
                    {profilePictureFile && (
                      <button
                        type="button"
                        onClick={handleProfilePictureUpload}
                        disabled={uploadingProfilePicture}
                          className="business-btn business-btn-primary business-upload-btn"
                        >
                          {uploadingProfilePicture ? (
                            <>
                              <span className="business-spinner"></span>
                              <span>Uploading...</span>
                            </>
                          ) : (
                            <>
                              <i className="fi fi-rr-upload"></i>
                              <span>Upload</span>
                            </>
                          )}
                      </button>
                    )}
                  </div>

                    {(profilePicturePreview || formData.profile_picture) && (
                      <div className="business-image-preview">
                        {profilePicturePreview ? (
                          <img src={profilePicturePreview} alt="Profile picture preview" className="business-preview-image business-preview-circle" />
                        ) : formData.profile_picture ? (
                          <>
                            <p className="business-upload-success">
                              <i className="fi fi-rr-check-circle"></i>
                              Profile picture uploaded successfully
                            </p>
                            <img src={formData.profile_picture} alt="Uploaded profile picture" className="business-preview-image business-preview-circle" />
                          </>
                        ) : null}
                    </div>
                  )}

                    <input type="hidden" name="profile_picture" value={formData.profile_picture} />
                </div>

                {/* Flash Image Upload */}
                  <div className="business-form-group">
                    <label htmlFor="flash_image_file" className="business-form-label">
                      <i className="fi fi-rr-picture"></i>
                      Flash Image (Max 5MB)
                  </label>
                  
                    <div className="business-upload-wrapper">
                    <input
                      type="file"
                      id="flash_image_file"
                      name="flash_image_file"
                      accept="image/*"
                      onChange={handleFlashImageChange}
                        className="business-file-input"
                    />
                      <label htmlFor="flash_image_file" className="business-file-label">
                        <i className="fi fi-rr-cloud-upload"></i>
                        <span>{flashImageFile ? flashImageFile.name : 'Choose Flash Image'}</span>
                    </label>
                    {flashImageFile && (
                      <button
                        type="button"
                        onClick={handleFlashImageUpload}
                        disabled={uploadingFlash}
                          className="business-btn business-btn-primary business-upload-btn"
                        >
                          {uploadingFlash ? (
                            <>
                              <span className="business-spinner"></span>
                              <span>Uploading...</span>
                            </>
                          ) : (
                            <>
                              <i className="fi fi-rr-upload"></i>
                              <span>Upload</span>
                            </>
                          )}
                      </button>
                    )}
                  </div>

                    {(flashImagePreview || formData.flash_image) && (
                      <div className="business-image-preview">
                        {flashImagePreview ? (
                          <img src={flashImagePreview} alt="Flash image preview" className="business-preview-image" />
                        ) : formData.flash_image ? (
                          <>
                            <p className="business-upload-success">
                              <i className="fi fi-rr-check-circle"></i>
                              Flash image uploaded successfully
                            </p>
                            <img src={formData.flash_image} alt="Uploaded flash image" className="business-preview-image" />
                          </>
                        ) : null}
                    </div>
                  )}

                    <input type="hidden" name="flash_image" value={formData.flash_image} />
                    
                    <div className="business-form-help" style={{ fontSize: '0.8rem', marginTop: '8px' }}>
                      <i className="fi fi-rr-info"></i>
                      <span>Banner image (1200x400px)</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Basic Information Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-user"></i>
                  Basic Information
                </h3>
                
                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="name" className="business-form-label">
                      <i className="fi fi-rr-id-card"></i>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter full name"
                      className="business-form-input"
                      />
                    </div>

                  <div className="business-form-group">
                    <label htmlFor="party_name" className="business-form-label">
                      <i className="fi fi-rr-flag"></i>
                      Party Name *
                    </label>
                    <input
                      type="text"
                      id="party_name"
                      name="party_name"
                      value={formData.party_name}
                      onChange={handleChange}
                      required
                      placeholder="Enter party name"
                      className="business-form-input"
                    />
                  </div>
                  </div>

                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="mobile_number" className="business-form-label">
                      <i className="fi fi-rr-phone-call"></i>
                      Mobile Number *
                    </label>
                  <input
                      type="tel"
                      id="mobile_number"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleChange}
                      required
                      placeholder="Enter mobile number"
                      className="business-form-input"
                  />
                </div>

                  <div className="business-form-group">
                    <label htmlFor="email_id" className="business-form-label">
                      <i className="fi fi-rr-envelope"></i>
                      Email ID *
                    </label>
                    <input
                      type="email"
                      id="email_id"
                      name="email_id"
                      value={formData.email_id}
                      onChange={handleChange}
                      required
                      placeholder="Enter email address"
                      className="business-form-input"
                    />
                </div>
                </div>

                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="address" className="business-form-label">
                      <i className="fi fi-rr-marker"></i>
                      Address *
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      placeholder="Enter full address"
                      className="business-form-input business-form-textarea"
                      rows="3"
                    />
                </div>
                </div>
              </div>

              {/* About & Description Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-document"></i>
                  About & Description
                </h3>
                
                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="about_us" className="business-form-label">
                      <i className="fi fi-rr-file-text"></i>
                      About Us *
                    </label>
                    <textarea
                      id="about_us"
                      name="about_us"
                      value={formData.about_us}
                      onChange={handleChange}
                      required
                      placeholder="Write about yourself, your political journey, achievements..."
                      className="business-form-input business-form-textarea"
                      rows="5"
                    />
                  </div>

                  <div className="business-form-group">
                    <label htmlFor="description" className="business-form-label">
                      <i className="fi fi-rr-align-left"></i>
                      Detailed Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Provide a detailed description of your political profile, vision, and goals..."
                      className="business-form-input business-form-textarea"
                      rows="5"
                    />
                  </div>
                </div>
              </div>

              {/* Slider Images - Same as CreateProfile */}
              <div style={{ marginBottom: '28px', paddingTop: '28px', borderTop: '1px solid #334155' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '20px', color: '#fff' }}>Slider Images</h3>
                <div style={{ marginBottom: '16px' }}>
                  <input type="file" id="slider_files" name="slider_files" accept="image/*" multiple onChange={handleSliderFileChange} style={{ display: 'none' }} />
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <label htmlFor="slider_files" style={{ flex: 1, padding: '12px 16px', background: '#334155', border: '1px solid #475569', borderRadius: '12px', color: '#fff', fontSize: '0.95rem', cursor: 'pointer', textAlign: 'center', transition: 'all 0.3s' }} onMouseEnter={(e) => e.target.style.background = '#475569'} onMouseLeave={(e) => e.target.style.background = '#334155'}>
                      Choose Slider Images (Multiple)
                    </label>
                    {sliderFiles.length > 0 && (
                      <button type="button" onClick={handleSliderUpload} disabled={uploadingSlider} style={{ padding: '12px 24px', background: uploadingSlider ? '#475569' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', border: 'none', borderRadius: '12px', cursor: uploadingSlider ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.95rem', opacity: uploadingSlider ? 0.7 : 1 }}>
                        {uploadingSlider ? 'Uploading...' : `Upload ${sliderFiles.length} Image(s)`}
                      </button>
                    )}
                  </div>
                  {sliderFiles.length > 0 && (
                    <div style={{ marginBottom: '16px', padding: '12px', background: '#0f172a', borderRadius: '8px' }}>
                      <p style={{ color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '8px' }}>Selected Files ({sliderFiles.length}):</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {sliderFiles.map((file, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#1e293b', borderRadius: '6px' }}>
                            <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>{file.name}</span>
                            <button type="button" onClick={() => removeSliderFile(index)} style={{ padding: '4px 8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>×</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {formData.slider_images && Array.isArray(formData.slider_images) && formData.slider_images.length > 0 && (
                  <div>
                    <p style={{ color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '12px' }}>Uploaded Slider Images ({formData.slider_images.length}):</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                      {formData.slider_images.map((img, index) => (
                        <div key={index} style={{ position: 'relative', background: '#0f172a', borderRadius: '8px', overflow: 'hidden' }}>
                          <img src={img} alt={`Slider ${index + 1}`} style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          <button type="button" onClick={() => removeSliderImage(index)} style={{ position: 'absolute', top: '8px', right: '8px', padding: '6px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' }}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Image Gallery - Same as CreateProfile */}
              <div style={{ marginBottom: '28px', paddingTop: '28px', borderTop: '1px solid #334155' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '20px', color: '#fff' }}>Image Gallery</h3>
                <div style={{ marginBottom: '16px' }}>
                  <input type="file" id="gallery_files" name="gallery_files" accept="image/*" multiple onChange={handleGalleryFileChange} style={{ display: 'none' }} />
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <label htmlFor="gallery_files" style={{ flex: 1, padding: '12px 16px', background: '#334155', border: '1px solid #475569', borderRadius: '12px', color: '#fff', fontSize: '0.95rem', cursor: 'pointer', textAlign: 'center', transition: 'all 0.3s' }} onMouseEnter={(e) => e.target.style.background = '#475569'} onMouseLeave={(e) => e.target.style.background = '#334155'}>
                      Choose Gallery Images (Multiple)
                    </label>
                    {galleryFiles.length > 0 && (
                      <button type="button" onClick={handleGalleryUpload} disabled={uploadingGallery} style={{ padding: '12px 24px', background: uploadingGallery ? '#475569' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', border: 'none', borderRadius: '12px', cursor: uploadingGallery ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.95rem', opacity: uploadingGallery ? 0.7 : 1 }}>
                        {uploadingGallery ? 'Uploading...' : `Upload ${galleryFiles.length} Image(s)`}
                      </button>
                    )}
                  </div>
                  {galleryFiles.length > 0 && (
                    <div style={{ marginBottom: '16px', padding: '12px', background: '#0f172a', borderRadius: '8px' }}>
                      <p style={{ color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '8px' }}>Selected Files ({galleryFiles.length}):</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {galleryFiles.map((file, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#1e293b', borderRadius: '6px' }}>
                            <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>{file.name}</span>
                            <button type="button" onClick={() => removeGalleryFile(index)} style={{ padding: '4px 8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>×</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {formData.image_gallery && Array.isArray(formData.image_gallery) && formData.image_gallery.length > 0 && (
                  <div>
                    <p style={{ color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '12px' }}>Uploaded Gallery Images ({formData.image_gallery.length}):</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                      {formData.image_gallery.map((img, index) => (
                        <div key={index} style={{ position: 'relative', background: '#0f172a', borderRadius: '8px', overflow: 'hidden' }}>
                          <img src={img} alt={`Gallery ${index + 1}`} style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          <button type="button" onClick={() => removeGalleryImage(index)} style={{ position: 'absolute', top: '8px', right: '8px', padding: '6px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' }}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Video Gallery Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-video"></i>
                  Video Gallery
                </h3>
                
                <div className="business-form-group">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                    <input 
                      type="text" 
                      value={newVideo.title} 
                      onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })} 
                      placeholder="Video Title..." 
                      className="business-form-input"
                    />
                    <input 
                      type="url" 
                      value={newVideo.url} 
                      onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })} 
                      placeholder="Video URL (YouTube, Vimeo, etc.)..." 
                      className="business-form-input"
                    />
                    <button type="button" onClick={addVideo} className="business-btn business-btn-primary">
                      <i className="fi fi-rr-plus"></i>
                      <span>Add Video</span>
                    </button>
                </div>
                  
                {formData.video_gallery && Array.isArray(formData.video_gallery) && formData.video_gallery.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {formData.video_gallery.map((video, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>{video.title}</div>
                            <div style={{ color: '#64748b', fontSize: '0.85rem', wordBreak: 'break-all' }}>{video.url}</div>
                        </div>
                          <button type="button" onClick={() => removeVideo(index)} className="business-btn business-btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
                </div>
              </div>

              {/* Instagram Reels Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-brands-instagram"></i>
                  Instagram Reels
                </h3>
                
                <div className="business-form-group">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                    <input 
                      type="text" 
                      value={newInstagramReel.title} 
                      onChange={(e) => setNewInstagramReel({ ...newInstagramReel, title: e.target.value })} 
                      placeholder="Reel Title..." 
                      className="business-form-input"
                    />
                    <input 
                      type="url" 
                      value={newInstagramReel.url} 
                      onChange={(e) => setNewInstagramReel({ ...newInstagramReel, url: e.target.value })} 
                      placeholder="Instagram Reel URL (https://instagram.com/reel/...)" 
                      className="business-form-input"
                    />
                    <button type="button" onClick={addInstagramReel} className="business-btn business-btn-primary">
                      <i className="fi fi-rr-plus"></i>
                      <span>Add Instagram Reel</span>
                    </button>
                  </div>
                  
                  {formData.instagram_reels && Array.isArray(formData.instagram_reels) && formData.instagram_reels.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {formData.instagram_reels.map((reel, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>{reel.title}</div>
                            <div style={{ color: '#64748b', fontSize: '0.85rem', wordBreak: 'break-all' }}>{reel.url}</div>
                          </div>
                          <button type="button" onClick={() => removeInstagramReel(index)} className="business-btn business-btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Recent News Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-newspaper"></i>
                  Recent News
                </h3>
                
                <div className="business-form-group">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                    <input 
                      type="text" 
                      value={newNews.title} 
                      onChange={(e) => setNewNews({ ...newNews, title: e.target.value })} 
                      placeholder="News Title..." 
                      className="business-form-input"
                    />
                  
                  {/* News Image Upload */}
                  <div>
                      <label htmlFor="news_image_file" className="business-form-label">
                        <i className="fi fi-rr-picture"></i>
                      News Image (Optional, Max 5MB)
                    </label>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <input
                        type="file"
                        id="news_image_file"
                        name="news_image_file"
                        accept="image/*"
                        onChange={handleNewsImageChange}
                          className="business-file-input"
                      />
                        <label htmlFor="news_image_file" className="business-file-label">
                          <i className="fi fi-rr-cloud-upload"></i>
                          <span>{newsImageFile ? newsImageFile.name : 'Choose News Image'}</span>
                      </label>
                      {newsImageFile && (
                        <button
                          type="button"
                          onClick={handleNewsImageUpload}
                          disabled={uploadingNewsImage}
                            className="business-btn business-btn-primary business-upload-btn"
                          >
                            {uploadingNewsImage ? (
                              <>
                                <span className="business-spinner"></span>
                                <span>Uploading...</span>
                              </>
                            ) : (
                              <>
                                <i className="fi fi-rr-upload"></i>
                                <span>Upload</span>
                              </>
                            )}
                        </button>
                      )}
                    </div>
                    
                    {/* Preview */}
                    {(newsImagePreview || newNews.image) && (
                      <div style={{ marginTop: '12px', textAlign: 'center' }}>
                          <img
                            src={newsImagePreview || newNews.image}
                            alt="News preview"
                            style={{
                              width: '100%',
                              maxWidth: '300px',
                              maxHeight: '200px',
                              borderRadius: '8px',
                              border: '2px solid #e2e8f0',
                              objectFit: 'cover',
                              display: 'block',
                              margin: '0 auto'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        {newNews.image && (
                            <p style={{ color: '#10b981', fontSize: '0.75rem', marginTop: '8px' }}>
                              ✓ Image uploaded successfully!
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                    <textarea 
                      value={newNews.description} 
                      onChange={(e) => setNewNews({ ...newNews, description: e.target.value })} 
                      placeholder="News Description..." 
                      rows="3" 
                      className="business-form-input business-form-textarea"
                    />
                    <input 
                      type="date" 
                      value={newNews.date} 
                      onChange={(e) => setNewNews({ ...newNews, date: e.target.value })} 
                      placeholder="News Date..." 
                      className="business-form-input"
                    />
                    <button type="button" onClick={addNews} className="business-btn business-btn-primary">
                      <i className="fi fi-rr-plus"></i>
                      <span>Add News</span>
                    </button>
                </div>
                  
                {formData.recent_news && Array.isArray(formData.recent_news) && formData.recent_news.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {formData.recent_news.map((news, index) => {
                      const hasImage = news.image && news.image.trim() !== '' && news.image !== null && news.image !== undefined;
                      
                      return (
                          <div key={index} style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              {hasImage && (
                                <div style={{ marginBottom: '12px' }}>
                                  <img
                                    src={news.image}
                                    alt={news.title || 'News'}
                                    style={{
                                      width: '100%',
                                      maxHeight: '200px',
                                      borderRadius: '8px',
                                      objectFit: 'cover',
                                        border: '2px solid #e2e8f0'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                                <div style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>{news.title}</div>
                                <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '4px' }}>{news.description}</div>
                                {news.date && <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Date: {news.date}</div>}
                            </div>
                              <button type="button" onClick={() => removeNews(index)} className="business-btn business-btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Remove</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>

              {/* Social Links Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-share"></i>
                  Social Media Links
                </h3>
                
                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="facebook" className="business-form-label">
                      <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
                        <circle cx="12" cy="12" r="11" fill="#1877F2"/>
                        <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z" fill="#FFFFFF"/>
                      </svg>
                      Facebook
                    </label>
                    <input
                      type="url"
                      id="facebook"
                      name="facebook"
                      value={formData.social_links?.facebook || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        social_links: { ...formData.social_links, facebook: e.target.value }
                      })}
                      placeholder="https://facebook.com/yourpage"
                      className="business-form-input"
                    />
                  </div>

                  <div className="business-form-group">
                    <label htmlFor="twitter" className="business-form-label">
                      <i className="fi fi-brands-twitter"></i>
                      Twitter
                    </label>
                    <input
                      type="url"
                      id="twitter"
                      name="twitter"
                      value={formData.social_links?.twitter || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        social_links: { ...formData.social_links, twitter: e.target.value }
                      })}
                      placeholder="https://twitter.com/yourhandle"
                      className="business-form-input"
                    />
                  </div>
                  </div>

                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="instagram" className="business-form-label">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="url(#instagram-gradient-edit)" style={{ marginRight: '8px' }}>
                        <defs>
                          <linearGradient id="instagram-gradient-edit" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#833AB4" />
                            <stop offset="50%" stopColor="#FD1D1D" />
                            <stop offset="100%" stopColor="#FCB045" />
                          </linearGradient>
                        </defs>
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4 2.209 0 4 1.791 4 4 0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      Instagram
                    </label>
                    <input
                      type="url"
                      id="instagram"
                      name="instagram"
                      value={formData.social_links?.instagram || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        social_links: { ...formData.social_links, instagram: e.target.value }
                      })}
                      placeholder="https://instagram.com/yourprofile"
                      className="business-form-input"
                    />
                  </div>

                  <div className="business-form-group">
                    <label htmlFor="youtube" className="business-form-label">
                      <i className="fi fi-brands-youtube"></i>
                      YouTube
                    </label>
                    <input
                      type="url"
                      id="youtube"
                      name="youtube"
                      value={formData.social_links?.youtube || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        social_links: { ...formData.social_links, youtube: e.target.value }
                      })}
                      placeholder="https://youtube.com/yourchannel"
                      className="business-form-input"
                    />
                  </div>
                  </div>

                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="linkedin" className="business-form-label">
                      <i className="fi fi-brands-linkedin"></i>
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      id="linkedin"
                      name="linkedin"
                      value={formData.social_links?.linkedin || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        social_links: { ...formData.social_links, linkedin: e.target.value }
                      })}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className="business-form-input"
                    />
                  </div>

                  <div className="business-form-group">
                    <label htmlFor="website" className="business-form-label">
                      <i className="fi fi-rr-globe"></i>
                      Website
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={formData.social_links?.website || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        social_links: { ...formData.social_links, website: e.target.value }
                      })}
                      placeholder="https://yourwebsite.com"
                      className="business-form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Survey Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-clipboard"></i>
                  Survey (सर्वेक्षण)
                </h3>
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px' }}>
                  {editingSurveyIndex !== null ? 'Edit Survey' : 'Create surveys to collect feedback from users for your election campaign'}
                </p>
                
                {/* Create New Survey Form */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                  <input
                    type="text"
                    value={newSurvey.title}
                    onChange={(e) => setNewSurvey({ ...newSurvey, title: e.target.value })}
                    placeholder="Survey Title (e.g., Election Feedback Survey)..."
                    className="business-form-input"
                  />
                  <textarea
                    value={newSurvey.description}
                    onChange={(e) => setNewSurvey({ ...newSurvey, description: e.target.value })}
                    placeholder="Survey Description (Optional)..."
                    rows="2"
                    className="business-form-input business-form-textarea"
                  />
                  
                  {/* Add Questions Section */}
                  <div style={{ marginTop: '12px', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <label className="business-form-label" style={{ marginBottom: '8px' }}>
                      Add Questions
                    </label>
                    <input
                      type="text"
                      value={newQuestion.question}
                      onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                      placeholder="Enter question..."
                      className="business-form-input"
                      style={{ marginBottom: '10px' }}
                    />
                    <select
                      value={newQuestion.type}
                      onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value, options: e.target.value === 'text' ? [] : newQuestion.options })}
                      className="business-form-input"
                      style={{ marginBottom: '10px' }}
                    >
                      <option value="text">Text Answer</option>
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="yes_no">Yes/No</option>
                    </select>
                    
                    {/* Options for Multiple Choice */}
                    {newQuestion.type === 'multiple_choice' && (
                      <div style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            placeholder="Add option..."
                            className="business-form-input"
                            style={{ flex: 1 }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addOption();
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={addOption}
                            className="business-btn business-btn-secondary"
                            style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                          >
                            Add Option
                          </button>
                        </div>
                        {newQuestion.options.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {newQuestion.options.map((opt, idx) => (
                              <div key={idx} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 10px',
                                background: '#e2e8f0',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                color: '#1e293b'
                              }}>
                                <span>{opt}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewQuestion({
                                      ...newQuestion,
                                      options: newQuestion.options.filter((_, i) => i !== idx)
                                    });
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    padding: '0 4px'
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <button
                      type="button"
                      onClick={addQuestion}
                      className="business-btn business-btn-primary"
                      style={{ width: '100%', padding: '10px 16px', fontSize: '0.9rem' }}
                    >
                      <i className="fi fi-rr-plus"></i>
                      <span>Add Question</span>
                    </button>
                  </div>
                  
                  {/* Added Questions List */}
                  {newSurvey.questions.length > 0 && (
                    <div style={{ marginTop: '12px', padding: '12px', background: '#fff', borderRadius: '8px' }}>
                      <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '8px' }}>
                        Questions ({newSurvey.questions.length}):
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {newSurvey.questions.map((q, idx) => (
                          <div key={idx} style={{
                            padding: '10px',
                            background: '#f8fafc',
                            borderRadius: '6px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#1e293b', fontSize: '0.9rem', marginBottom: '4px' }}>
                                {q.question}
                              </div>
                              <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                Type: {q.type === 'text' ? 'Text' : q.type === 'multiple_choice' ? 'Multiple Choice' : 'Yes/No'}
                                {q.options.length > 0 && ` (${q.options.length} options)`}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeQuestion(idx)}
                              className="business-btn business-btn-secondary"
                              style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <button
                    type="button"
                    onClick={addSurvey}
                    disabled={newSurvey.title.trim() === '' || newSurvey.questions.length === 0}
                    className="business-btn business-btn-primary"
                    style={{ opacity: newSurvey.title.trim() === '' || newSurvey.questions.length === 0 ? 0.6 : 1 }}
                  >
                    <i className="fi fi-rr-plus"></i>
                    <span>{editingSurveyIndex !== null ? 'Update Survey' : 'Add Survey'}</span>
                  </button>
                  {editingSurveyIndex !== null && (
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="business-btn business-btn-secondary"
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>
                
                {/* Added Surveys List */}
                {Array.isArray(formData.surveys) && formData.surveys.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>
                      Created Surveys ({formData.surveys.length})
                  </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {formData.surveys.map((survey, index) => (
                        <div key={index} style={{ 
                          padding: '16px', 
                          background: '#f8fafc', 
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>{survey.title}</div>
                              {survey.description && (
                                <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '8px' }}>{survey.description}</div>
                              )}
                              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                                Questions: {survey.questions ? survey.questions.length : 0}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={() => editSurvey(index)}
                                disabled={editingSurveyIndex !== null && editingSurveyIndex !== index}
                                className="business-btn business-btn-secondary"
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '0.85rem',
                                  opacity: (editingSurveyIndex !== null && editingSurveyIndex !== index) ? 0.5 : 1
                                }}
                              >
                                {editingSurveyIndex === index ? 'Editing...' : 'Edit'}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to delete this survey?')) {
                                    removeSurvey(index);
                                  }
                                }}
                                disabled={editingSurveyIndex === index}
                                className="business-btn business-btn-secondary"
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '0.85rem',
                                  background: '#ef4444',
                                  opacity: editingSurveyIndex === index ? 0.5 : 1
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    </div>
                  )}
              </div>

              {/* Form Actions */}
              <div className="business-form-actions">
                <button 
                  type="submit" 
                  className="business-btn business-btn-primary" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="business-spinner"></span>
                      <span>Updating Profile...</span>
                    </>
                  ) : (
                    <>
                      <span>Update Profile</span>
                      <i className="fi fi-rr-arrow-right"></i>
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  className="business-btn business-btn-secondary" 
                  onClick={() => navigate(`/profile/${user?.username}`)}
                >
                  <span>Cancel</span>
                </button>
              </div>

              {/* Additional Sections - Slider Images, Gallery, etc. */}
              {/* Note: These sections can be added here if needed in the future */}
            </form>
          </div>
    </AdminLayout>
  );
};

export default EditProfile;

